import { StringParam, useQueryParams } from 'use-query-params';
import {
  useNavigate as useRouterNavigate,
  NavigateFunction,
  To,
  NavigateOptions,
  Path,
} from 'react-router-dom';
import { Routes } from 'fnRoutes';
import { useCurrentUserRole } from 'hooks/api/useUser';

export const useSpoof = (): {
  spoofUserID?: string;
  isAdminSpoof: boolean;
} => {
  // spoofUserID is used to spoof the user id for admin users
  const [query] = useQueryParams({
    spoofUserID: StringParam,
  });
  return {
    spoofUserID: query.spoofUserID || undefined,
    isAdminSpoof: !!query.spoofUserID,
  };
};

const generatePathWithSpoof = (
  path: string,
  spoofUserID: string = '',
): string => {
  const url = new URL(`${window.location.origin}${path}`);
  url.pathname = `/spoof${url.pathname}`;
  url.searchParams.set('spoofUserID', spoofUserID);

  return `${url.pathname}${url.search}`;
};

const generateLocationObjectWithSpoof = (
  loc: Partial<Path>,
  spoofUserID: string = '',
): To => {
  const search = new URLSearchParams(loc.search);
  search.set('spoofUserID', spoofUserID);
  return `/spoof${loc.pathname}?${search.toString()}`;
};

export const usePathWithSpoof = (path: string) => {
  const { spoofUserID, isAdminSpoof } = useSpoof();
  if (!isAdminSpoof || path.includes('spoof')) {
    return path;
  }
  return generatePathWithSpoof(path, spoofUserID);
};

export const useNavigate = (): NavigateFunction => {
  const navigate = useRouterNavigate();
  const { spoofUserID, isAdminSpoof } = useSpoof();
  return (to: To | number, options?: NavigateOptions): void => {
    if (typeof to === 'number') {
      return navigate(to);
    }
    if (!isAdminSpoof || !spoofUserID) {
      return navigate(to, options);
    }
    if (typeof to === 'string') {
      const newPath = generatePathWithSpoof(to, spoofUserID);
      return navigate(newPath, options);
    }
    return navigate(generateLocationObjectWithSpoof(to, spoofUserID), options);
  };
};

export const useHomePath = () => {
  const { isAdmin } = useCurrentUserRole();
  return isAdmin ? Routes.ADMIN_HOME : Routes.CUSTOMER_HOME;
};
